import { router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PhoneInput, parsePhone, formatFullPhone } from '@/components/ui/phone-input'
import type { PageProps } from '@/types'
import { EVENTS, trackEvent } from '@/lib/posthog'

interface SignupProps extends PageProps {
  step: 'request' | 'verify'
  phone: string | null
  name?: string
  organization_name?: string
  errors: Record<string, string>
}

export default function Signup({ flash, step, phone, name: initialName, organization_name: initialOrgName, errors: serverErrors }: SignupProps) {
  const parsed = parsePhone(phone ?? '')
  const [processing, setProcessing] = useState(false)
  const [userName, setUserName] = useState(initialName ?? '')
  const [organizationName, setOrganizationName] = useState(initialOrgName ?? '')
  const [countryCode, setCountryCode] = useState(parsed.countryCode)
  const [localPhone, setLocalPhone] = useState(parsed.localPhone)
  const [code, setCode] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    trackEvent(EVENTS.SIGNUP_STARTED, {
      utm_source: params.get('utm_source') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      cta_location: params.get('cta_location') || undefined,
    })
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post('/users', {
      user: {
        name: userName,
        organization_name: organizationName,
        phone: formatFullPhone(countryCode, localPhone),
        code,

      },
    }, {
      onFinish: () => setProcessing(false),
    })
  }

  const mergedErrors = { ...serverErrors }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Crear cuenta</CardTitle>
          <CardDescription>Comienza a gestionar tu negocio</CardDescription>
        </CardHeader>
        <CardContent>
          {flash?.alert && (
            <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {flash.alert}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Tu nombre</Label>
              <Input
                id="name"
                type="text"
                placeholder="Juan Pérez"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                disabled={step === 'verify'}
              />
              {mergedErrors.name && (
                <p className="text-sm text-destructive">{mergedErrors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization_name">Nombre de tu negocio</Label>
              <Input
                id="organization_name"
                type="text"
                placeholder="Mi Salón de Belleza"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                required
                disabled={step === 'verify'}
              />
              {mergedErrors.organization_name && (
                <p className="text-sm text-destructive">{mergedErrors.organization_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono (WhatsApp)</Label>
              <PhoneInput
                countryCode={countryCode}
                localPhone={localPhone}
                onCountryCodeChange={setCountryCode}
                onLocalPhoneChange={setLocalPhone}
                disabled={step === 'verify'}
              />
              {mergedErrors.phone && (
                <p className="text-sm text-destructive">{mergedErrors.phone}</p>
              )}
            </div>

            {step === 'verify' && (
              <div className="space-y-2">
                <Label htmlFor="code">Código</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                {mergedErrors.code && (
                  <p className="text-sm text-destructive">{mergedErrors.code}</p>
                )}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={processing}>
              {processing
                ? 'Procesando...'
                : step === 'verify'
                  ? 'Verificar'
                  : 'Enviar código'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">¿Ya tienes cuenta? </span>
            <a
              href="/users/sign_in"
              onClick={() => trackEvent(EVENTS.MARKETING_LOGIN_CLICKED, {
                location: 'signup_page',
                label: 'Ingresar',
                href: '/users/sign_in',
              })}
              className="text-primary hover:underline"
            >
              Ingresar
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
